import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285938694_musik-klar-mockup-caja-6-volymer-693a5242.webp';
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sidor i huvudvolymerna'
}, {
  value: '6',
  label: 'Digitala musikvolymer'
}, {
  value: '9',
  label: 'Årskurser – från 1 till 9'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}, {
  value: '1',
  label: 'Komplett undervisningssystem'
}];
export const differenceStats = [{
  value: 'Årskurs 1–9',
  label: 'Material organiserat i tre stadier'
}, {
  value: '6 digitala volymer',
  label: 'Från rytm och sång till komposition och musikhistoria'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att använda digitalt eller skriva ut i musiksalen'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällarna försvinner',
  text: 'Timmar framför datorn för att planera nästa veckas musiklektioner – och du är fortfarande inte klar.'
}, {
  title: 'Timmar av letande',
  text: 'Aktiviteter och låtar från olika webbplatser är för lätta, för svåra eller måste anpassas innan de går att använda.'
}, {
  title: 'Arbetsbladen skapas från grunden',
  text: 'Noter, rytmrutor, lyssningsfrågor och facit – det som skulle ta minuter blir en lång arbetsuppgift.'
}, {
  title: 'Svårt att göra musiken visuell',
  text: 'Rytmkort, notbilder, instrumentbilder och lyssningskartor tar tid att ta fram för varje nytt moment.'
}, {
  title: 'Bedömning och uppföljning tar tid',
  text: 'Spelprov, lyssningsuppgifter och uppföljning av elevernas utveckling blir ännu mer arbete efter skoltid.'
}, {
  title: 'Stress inför nästa lektion',
  text: 'När tiden inte räcker till blir det lätt att improvisera och få ihop material i sista minuten.'
}];
export const afterItems = ['Ett organiserat musikpaket för hela grundskolan', 'Lektionsupplägg som är enkla att använda', 'Färdiga musikaktiviteter', 'Arbetsblad som kan skrivas ut direkt', 'Visuella rytm- och notövningar', 'Aktiviteter för sång, lyssning och spel', 'Musikspel som skapar variation', 'Bedömningsmaterial och facit', 'Planeringsstöd för hela läsåret'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Söndagarna går åt till planering',
  text: 'Lägger du delar av helgen på att skapa musiklektioner inför kommande vecka?'
}, {
  icon: FolderSearchIcon,
  title: 'Du vet inte vilken aktivitet du ska använda',
  text: 'Letar du ständigt efter övningar och låtar som passar elevernas nivå?'
}, {
  icon: LightbulbIcon,
  title: 'Du vill ha tydligare förklaringar',
  text: 'Behöver du mer visuellt material för att förklara rytm, noter och musikbegrepp på ett enkelt sätt?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektioner, uppgifter och aktiviteter redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna behöver mer variation',
  text: 'Vill du använda fler spel, rytmlekar och praktiska aktiviteter i undervisningen?'
}, {
  icon: LayoutGridIcon,
  title: 'Du behöver en tydligare struktur',
  text: 'Vill du ha materialet samlat så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Åk 1–3',
  title: 'Rytm, puls & sång',
  tagline: 'Ge eleverna en trygg grund i musiken.',
  intro: 'Den första volymen fokuserar på puls, rytm, röst och sång – grunden för allt musicerande.',
  items: ['Puls och takt', 'Enkla rytmer', 'Rytmlekar och klappövningar', 'Sång och röstövningar', 'Rörelse till musik', 'Kroppsperkussion', 'Utskrivbara arbetsblad', 'Musikspel', 'Repetition och bedömning'],
  outro: 'Färdigt material som gör grundläggande musik konkret och lustfylld.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Åk 1–3',
  title: 'Lyssna, upptäck & instrument',
  tagline: 'Hjälp eleverna att upptäcka musiken i världen omkring dem.',
  intro: 'Den andra volymen innehåller visuella och praktiska aktiviteter för lyssning, ljud och instrument.',
  items: ['Ljud och tystnad', 'Starkt och svagt', 'Högt och lågt', 'Instrumentfamiljer', 'Lyssningsövningar', 'Musik och känslor', 'Praktiska musikaktiviteter', 'Visuella arbetsblad', 'Repetition och bedömning'],
  outro: 'Färdiga övningar där eleverna får lyssna, utforska, spela och skapa.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Åk 4–6',
  title: 'Noter, melodi & musikteori',
  tagline: 'Gör noter och musikteori enklare att förstå.',
  intro: 'Den tredje volymen hjälper eleverna att läsa noter och se sambanden mellan rytm, melodi och notbild.',
  items: ['Notsystemet', 'Notvärden och pauser', 'Taktarter', 'Melodi och skalor', 'Tonarter i praktiken', 'Musikaliska begrepp', 'Visuella notövningar', 'Gehörsövningar', 'Utskrivbara arbetsblad', 'Bedömningar och facit'],
  outro: 'Tydliga exempel och aktiviteter för musikundervisningen i årskurs 4–6.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 04',
  level: 'Åk 4–6',
  title: 'Spela tillsammans & skapa musik',
  tagline: 'Utveckla elevernas musikaliska samspel.',
  intro: 'Den fjärde volymen kombinerar spel, samspel och eget skapande med visuella uppgifter och praktiska aktiviteter.',
  items: ['Spel på rytminstrument', 'Enkla ackord', 'Samspel i grupp', 'Ostinaton och komp', 'Improvisation', 'Skapa egna rytmer', 'Komponera en melodi', 'Musik och digitala verktyg', 'Musikspel', 'Bedömningsuppgifter'],
  outro: 'Ett praktiskt stöd för att få hela klassen att spela och skapa tillsammans.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Åk 7–9',
  title: 'Ensemble, ackord & komposition',
  tagline: 'Hjälp eleverna att utvecklas som musiker steg för steg.',
  intro: 'Den femte volymen innehåller färdiga aktiviteter för ensemblespel, ackord, låtskrivande och arrangemang.',
  items: ['Ensemblespel', 'Ackord och ackordföljder', 'Gitarr, piano och bas', 'Trummor och komp', 'Låtform och struktur', 'Låtskrivande', 'Arrangera en låt', 'Inspelning och produktion', 'Musikaliska uttryck', 'Repetition och bedömning'],
  outro: 'Visuella och strukturerade resurser för musikundervisningen på högstadiet.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 06',
  level: 'Åk 7–9',
  title: 'Musikhistoria, genrer & bedömning',
  tagline: 'Låt eleverna fördjupa sina kunskaper och träna musikaliskt resonemang.',
  intro: 'Den sjätte volymen innehåller musikhistoria, genrer, musikens roll i samhället och färdigt bedömningsstöd.',
  items: ['Musikhistorins epoker', 'Genrer och stilar', 'Svensk musik och folkmusik', 'Musik i samhället', 'Musikanalys', 'Lyssningsuppgifter', 'Musikaliska resonemang', 'Spelprov och redovisningar', 'Bedömningsmatriser', 'Facit och bedömningsstöd'],
  outro: 'Ett komplett komplement för fördjupning, analys och bedömning.',
  note: 'Övningarna är självständigt framtagna och är inte officiellt material från Skolverket.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitala volymer för årskurs 1–9',
  text: 'Ett komplett musikpaket som hjälper dig att planera och genomföra undervisningen under hela läsåret. Varje volym innehåller 40 sidor med musikmaterial, övningar och klassrumsaktiviteter.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Färdiga lektioner',
  text: 'Slipp börja från ett tomt dokument. Tydliga lektionsupplägg, exempel och aktiviteter hjälper dig att komma igång snabbare.'
}, {
  icon: FileTextIcon,
  title: 'Utskrivbara arbetsblad',
  text: 'Musikuppgifter som kan skrivas ut och användas direkt – för självständigt arbete, repetition och extra träning.'
}, {
  icon: ShapesIcon,
  title: 'Visuella musikövningar',
  text: 'Rytmkort, notbilder, instrumentbilder och lyssningskartor gör musiken mer konkret och lättare att förstå.'
}, {
  icon: DicesIcon,
  title: 'Musikspel och aktiviteter',
  text: 'Spel, rytmlekar och praktiska övningar ger eleverna fler sätt att träna och använda sina kunskaper.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömningar och facit',
  text: 'Färdiga bedömningsuppgifter och lösningar som stöd för rättning och uppföljning.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplanering',
  text: 'Stöd för att organisera musikundervisningen och välja arbetsområden utifrån din planering.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285892243_musik-klar-v1-sida-6-folj-pulssparet-4770d1e8.webp', label: 'Arbetsblad · Sida 6', title: 'Följ pulsspåret', text: 'Ett utskrivbart arbetsblad där eleverna följer pulsen från start till slut – med händer, fötter och klapp.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285895966_musik-klar-v1-sida-5-hitta-pulsen-792b1b10.webp', label: 'Praktisk lektion · Sida 5', title: 'Lektion 1: Hitta pulsen', text: 'En färdig lektion minut för minut med mål, start, huvudaktivitet, tillämpning och avslutning.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285899828_musik-klar-v1-sida-3-fyra-nycklar-96fff60e.webp', label: 'Visuell lektion · Sida 3', title: 'Fyra nycklar till musiken', text: 'Puls, rytm, tempo och dynamik förklarade visuellt – så att eleverna förstår musikens grunder direkt.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285903623_musik-klar-v1-sida-33-musiklekar-885f66c2.webp', label: 'Musiklekar · Sida 33', title: 'Sex snabba musiklekar', text: 'Färdiga lekar på 3–5 minuter som skapar energi, fokus och variation i musiksalen.' },
  { src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/90c0599f-244f-459f-a268-ba1295b0fa15/1792285906921_musik-klar-v1-sida-36-det-har-kan-jag-98d38644.webp', label: 'Formativ bedömning · Sida 36', title: 'Det här kan jag!', text: 'Elevens självskattning med tydliga mål – ett enkelt underlag för uppföljning och bedömning.' }
];
export const pageTypes = ['Färdiga lektioner', 'Utskrivbara arbetsblad', 'Praktiska aktiviteter', 'Bedömningar och facit'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Musikstart på 5 minuter',
  text: '30 snabba startaktiviteter som får hela klassen fokuserad och redo för musiklektionen i årskurs 1–9.',
  items: ['Rytmuppvärmningar', 'Röst- och sångövningar', 'Lyssningsstarter', 'Rörelse till musik'],
  benefit: 'Mindre tid på att komma igång och mer tid till musik.'
}, {
  title: 'Vikariepaketet: Musik',
  text: '6 färdiga musiklektioner som en vikarie kan genomföra direkt – utan att kunna spela ett instrument.',
  items: ['Färdiga lektionsupplägg', 'Tydliga instruktioner', 'Utskrivbart material', 'Lektioner för åk 1–9'],
  benefit: 'Perfekt när du snabbt behöver lämna över en lektion.'
}, {
  title: 'Musik utan instrument',
  text: 'Skapa musik med kroppen, rösten och vardagliga föremål – även när instrumenten inte räcker till.',
  items: ['Kroppsperkussion', 'Röst och rytm', 'Musik med vardagsföremål', 'Övningar i grupp'],
  benefit: 'Mer musicerande i klassrummet utan att du behöver fler instrument.'
}, {
  title: 'Musikbingo och instrumentkort',
  text: 'Utskrivbara spel för att upptäcka instrument och musikbegrepp på ett engagerande sätt.',
  items: ['Musikbingo', 'Instrumentkort', 'Begreppskort', 'Spel i par och grupp'],
  benefit: 'Mer variation i musiksalen utan att du behöver skapa spelen själv.'
}, {
  title: 'Konsertpaketet',
  text: 'Planera, repetera och genomför en musikalisk klasskonsert – steg för steg.',
  items: ['Planeringsmall', 'Repetitionsschema', 'Roller och ansvar', 'Inbjudan och program'],
  benefit: 'Känn dig trygg inför varje konsert och uppträdande.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna MUSIK KLAR™',
  text: 'Ladda ner paketet och öppna volymen som passar din årskurs.'
}, {
  title: 'Välj lektion och material',
  text: 'Hitta rätt arbetsområde och välj de övningar, arbetsblad eller aktiviteter som passar dina elever.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom instruktionerna och välj det material du behöver.'
}, {
  title: 'Skriv ut och undervisa',
  text: 'Skriv ut arbetsbladen eller använd materialet digitalt – och gå in i musiksalen med en tydlig plan.'
}];
export const audienceItems = ['Undervisar i musik i årskurs 1–3, 4–6 eller 7–9', 'Vill ha ett helt läsårs musikmaterial organiserat', 'Vill slippa skapa varje arbetsblad från grunden', 'Behöver fler visuella rytm- och notövningar', 'Vill använda musikspel och praktiska aktiviteter', 'Vill ha enkel tillgång till bedömningsmaterial och facit', 'Vill spara tid på veckoplaneringen', 'Vill känna sig bättre förberedd inför undervisningen'];
export const timeValueTasks = ['Leta efter aktiviteter och låtar', 'Skriva övningar', 'Skapa visuella förklaringar', 'Förbereda aktiviteter', 'Bedöma och följa upp elevernas arbete'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Rytm, sång, lyssning, noter, samspel, komposition och musikhistoria'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Musikstart, vikariepaket, musik utan instrument, musikbingo och konsertpaket'
}, {
  label: 'För årskurs 1–9',
  detail: 'Färdiga lektioner, arbetsblad och facit'
}];
export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara arbetsblad', 'Ingen fysisk produkt skickas'];
export const PRICE = '299 kr';

// Only add authentic reviews of MUSIK KLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'MUSIK KLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de arbetsblad du behöver.'
}, {
  q: 'Är MUSIK KLAR™ anpassat för den svenska skolan?',
  a: 'Ja. Materialet är framtaget för musikundervisning i den svenska grundskolan, med utgångspunkt i det centrala innehållet i Lgr22. Det är ett fristående undervisningsmaterial och inte en officiell produkt från Skolverket.'
}, {
  q: 'Passar det för årskurs 1–9?',
  a: 'Ja. De sex volymerna är organiserade i tre stadier – årskurs 1–3, 4–6 och 7–9. Du väljer material utifrån den årskurs och det musikaliska område du undervisar i.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'MUSIK KLAR™ är utformat för att göra lektionsförberedelserna snabbare. Du kan öppna en volym, välja ett färdigt arbetsblad eller lektionsupplägg och gå igenom det på ungefär fem minuter. Tiden kan variera beroende på lektionsinnehåll, elevgrupp och behov av anpassning.'
}, {
  q: 'Räcker materialet för ett helt läsår?',
  a: 'MUSIK KLAR™ innehåller material och aktiviteter som kan användas under hela läsåret. Du väljer, kombinerar och anpassar resurserna efter din årsplanering och elevernas behov.'
}, {
  q: 'Finns det färdiga arbetsblad?',
  a: 'Ja. Materialet innehåller utskrivbara arbetsblad med uppgifter, övningar och visuella förklaringar.'
}, {
  q: 'Ingår facit?',
  a: 'Ja. Paketet innehåller facit och lösningar som stöd för rättning och genomgång.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. MUSIK KLAR™ innehåller varierade uppgifter och aktiviteter som du kan välja och anpassa utifrån elevernas kunskaper och behov.'
}, {
  q: 'Måste jag skriva ut allt?',
  a: 'Nej. Du väljer själv vilka sidor du skriver ut, och du kan även använda delar av materialet digitalt.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Kan jag spara filerna?',
  a: 'Ja. Du kan ladda ner och spara PDF-filerna för fortsatt användning enligt produktens användarvillkor.'
}, {
  q: 'Kan jag använda materialet på mobil eller surfplatta?',
  a: 'Ja. PDF-filerna kan öppnas på dator, surfplatta och mobil. För att granska layouten på arbetsbladen före utskrift rekommenderar vi dator eller surfplatta.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. MUSIK KLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom garantiperioden enligt villkoren.'
}];
export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];